
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Crown, Calendar, AlertTriangle, CheckCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isAndroidTWA } from "@/utils/runtimeContext";

const TrialStatusBanner = () => {
  const { user, getTrialInfo } = useAuth();

  if (!user) return null;
  if (isAndroidTWA()) return null;

  const trialInfo = getTrialInfo();

  // Para usuários gratuitos - período de teste
  if (user.plan === "free" && !user.isSubscribed) {
    if (trialInfo.status === 'trial_active') {
      // Teste ativo - cartão azul discreto
      return (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-blue-800 text-lg">
                  🎯 Período de Teste Ativo
                </h4>
                <p className="text-blue-600 text-sm">
                  {trialInfo.statusMessage} para explorar todas as funcionalidades
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-800">
                  {trialInfo.daysRemaining}
                </div>
                <div className="text-xs text-blue-600 font-medium">
                  dia{trialInfo.daysRemaining === 1 ? '' : 's'} restante{trialInfo.daysRemaining === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            {trialInfo.daysRemaining <= 1 && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-700 font-medium">
                    ⏰ Seu teste está acabando! Garanta acesso ilimitado:
                  </p>
                  <Link to="/planos">
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                      Ver Planos
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    } else {
      // Teste expirado - cartão vermelho
      return (
        <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50 shadow-lg mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-red-800 text-lg">
                  ⏰ Período de Teste Expirado
                </h4>
                <p className="text-red-600 text-sm">
                  Seus 3 dias de teste gratuito chegaram ao fim. Contrate um plano para continuar usando todas as funcionalidades.
                </p>
              </div>
              <Link to="/planos">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                  <Crown className="w-4 h-4 mr-2" />
                  Assinar Agora
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      );
    }
  }

  // Para usuários com planos pagos - mostrar dias restantes da assinatura
  if (user.isSubscribed && user.subscriptionEndDate) {
    if (trialInfo.status === 'subscription_active') {
      // Assinatura ativa - cartão verde
      return (
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 shadow-sm mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-green-800 text-lg">
                  ✅ Plano Ativo
                </h4>
                <p className="text-green-600 text-sm">
                  {trialInfo.statusMessage}
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-800">
                  {trialInfo.daysRemaining}
                </div>
                <div className="text-xs text-green-600 font-medium">
                  dia{trialInfo.daysRemaining === 1 ? '' : 's'} restante{trialInfo.daysRemaining === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    } else if (trialInfo.status === 'subscription_expiring') {
      // Assinatura expirando - cartão laranja
      return (
        <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50 shadow-lg mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-full">
                <Calendar className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-orange-800 text-lg">
                  ⚠️ Plano Expirando em Breve
                </h4>
                <p className="text-orange-600 text-sm">
                  {trialInfo.statusMessage}
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-800">
                  {trialInfo.daysRemaining}
                </div>
                <div className="text-xs text-orange-600 font-medium">
                  dia{trialInfo.daysRemaining === 1 ? '' : 's'} restante{trialInfo.daysRemaining === 1 ? '' : 's'}
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-orange-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-orange-700 font-medium">
                  🔄 Renove seu plano para continuar com acesso total:
                </p>
                <Link to="/planos">
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold">
                    Renovar Plano
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    } else {
      // Assinatura expirada - cartão vermelho
      return (
        <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50 shadow-lg mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-red-800 text-lg">
                  ❌ Assinatura Expirada
                </h4>
                <p className="text-red-600 text-sm">
                  Sua assinatura expirou. Renove para continuar acessando todos os recursos.
                </p>
              </div>
              <Link to="/planos">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                  <Crown className="w-4 h-4 mr-2" />
                  Renovar Agora
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      );
    }
  }

  return null;
};

export default TrialStatusBanner;
